import pickBy from 'lodash/pickBy'
import { createSelector } from 'reselect'

import { ALL_CATEGORY_IDS, CATEGORY_IDS, CATEGORY_NAMES, IMAGES } from '@/constants'
import { replaceCmsTokens } from '@/context/cms/utils'
import { normalizeSecureImages } from '@/context/utils/image-path'
import type { Breadcrumb, CategoryIdMap, Metadata, Region, RegionOverrides } from '@/types/app-types'

import type { BaseRegionSelectors, RegionState, ResponsiveImageUrls } from './types'

const baseRegionSelector = createSelector(
    ({ data: region, categoryId }: RegionState) => ({ region, categoryId }),
    ({ region, categoryId }): BaseRegionSelectors => {
        const {
            imageUrl: initialImageUrl,
            webPath,
            concertsOverrides,
            sportsOverrides,
            theaterOverrides,
        } = region ?? {}

        // Build a map of web paths for each category
        const categoryWebPathMap: CategoryIdMap<string> = ALL_CATEGORY_IDS.reduce((acc, currCategoryId) => {
            // Handle web paths with and without a trailing slash
            const categoryWebPath: string = webPath
                ? webPath.replace(/\/*$/, `/${CATEGORY_NAMES[currCategoryId].toLowerCase()}/`) // e.g. /region/usa/il/chicago-area-tickets/57/sports/
                : ''
            return { ...acc, [currCategoryId]: categoryWebPath }
        }, {}) as CategoryIdMap<string>

        // Get category overrides for the current categoryId
        let categoryOverrides: RegionOverrides | undefined
        if (categoryId) {
            // Build a map of category overrides for each category
            const categoryOverridesMap: CategoryIdMap<RegionOverrides | undefined> = {
                [CATEGORY_IDS.CONCERTS]: concertsOverrides,
                [CATEGORY_IDS.SPORTS]: sportsOverrides,
                [CATEGORY_IDS.THEATER]: theaterOverrides,
            }
            categoryOverrides = categoryOverridesMap[categoryId]
        }

        // Get responsive image URLs
        const imageUrl: string | undefined = normalizeSecureImages(initialImageUrl)
        const responsiveImageUrls: ResponsiveImageUrls = {
            imageUrl,
            desktopImageUrl: imageUrl ? `${imageUrl}?dimensions=840x336` : IMAGES.REGION_HERO_DESKTOP,
            mobileImageUrl: imageUrl ? `${imageUrl}?dimensions=735x288` : IMAGES.REGION_HERO_MOBILE,
        }

        return {
            region,
            categoryId,
            categoryWebPathMap,
            categoryOverrides,
            responsiveImageUrls,
        }
    },
)

export const regionSelector = createSelector(
    baseRegionSelector,
    ({ region }: BaseRegionSelectors): Region | undefined => region,
)

export const articleSelector = createSelector(
    baseRegionSelector,
    ({ region }: BaseRegionSelectors): string | undefined => replaceCmsTokens(region?.article, { region }),
)

export const categoryIdSelector = createSelector(
    baseRegionSelector,
    ({ categoryId }: BaseRegionSelectors): CATEGORY_IDS | undefined => categoryId,
)

export const categoryWebPathMapSelector = createSelector(
    baseRegionSelector,
    ({ categoryWebPathMap }: BaseRegionSelectors): CategoryIdMap<string> => categoryWebPathMap,
)

export const categoryOverridesSelector = createSelector(
    baseRegionSelector,
    ({ categoryOverrides }: BaseRegionSelectors): RegionOverrides | undefined => categoryOverrides,
)

export const responsiveImageUrlsSelector = createSelector(
    baseRegionSelector,
    ({ responsiveImageUrls }: BaseRegionSelectors): ResponsiveImageUrls => responsiveImageUrls,
)

export const listTitleSelector = createSelector(baseRegionSelector, ({ region }: BaseRegionSelectors): string => {
    if (!region) {
        return ''
    }

    let listTitle: string = region.listTitle

    // Get fallback list title
    if (!listTitle) {
        listTitle = region.name

        const stateAbbr: string = region.stateAbbr.toUpperCase()
        if (stateAbbr) {
            listTitle = `${stateAbbr} - ${listTitle}` // e.g. ON - Toronto
        }

        const countryAbbr: string = region.countryAbbr.toUpperCase()
        if (countryAbbr && countryAbbr !== 'US') {
            listTitle = `${countryAbbr} - ${listTitle}` // e.g. CAN - ON - Toronto
        }
    }

    return listTitle
})

export const pageTitleSelector = createSelector(
    baseRegionSelector,
    ({ region, categoryId, categoryOverrides }: BaseRegionSelectors): string => {
        const { name = '', title: customTitle } = region ?? {}
        return categoryId
            ? categoryOverrides?.title || `${name} ${CATEGORY_NAMES[categoryId]} Tickets` // e.g. Chicago Sports Tickets
            : customTitle || `${name} Event Tickets` // e.g. Chicago Event Tickets
    },
)

export const pageSubtitleSelector = createSelector(
    baseRegionSelector,
    ({ region, categoryId, categoryOverrides }: BaseRegionSelectors): string => {
        const { name = '', subtitle: customSubtitle } = region ?? {}
        return categoryId
            ? categoryOverrides?.subtitle || `${CATEGORY_NAMES[categoryId]} happening in ${name}` // e.g. Sports happening in Chicago
            : customSubtitle || `Sports, concert and theater events happening in ${name}` // e.g. Sports, concert and theater events happening in Chicago
    },
)

export const topPicksBlockTitleSelector = createSelector(
    baseRegionSelector,
    ({ region, categoryId }: BaseRegionSelectors): string => {
        const { name = '' } = region ?? {}
        return categoryId ? 'Trending Events' : `Top ${name} Events Tickets` // e.g. Top Chicago Events
    },
)

export const categoryBlockTitleMapSelector = createSelector(
    baseRegionSelector,
    ({ region }: BaseRegionSelectors): CategoryIdMap<string> => {
        // Build a map of category block titles for each category
        const { name = '' } = region ?? {}
        return ALL_CATEGORY_IDS.reduce(
            (acc, categoryId) => ({ ...acc, [categoryId]: `${name} ${CATEGORY_NAMES[categoryId]} Tickets` }), // e.g. Chicago Sports
            {},
        ) as CategoryIdMap<string>
    },
)

export const allProductionsBlockTitleSelector = createSelector(
    baseRegionSelector,
    ({ region, categoryId }: BaseRegionSelectors): string => {
        const { name = '' } = region ?? {}
        return categoryId
            ? `Upcoming ${CATEGORY_NAMES[categoryId]}` // e.g. Upcoming Sports
            : `All ${name} Events` // e.g. All Chicago Events
    },
)

export const breadcrumbsSelector = createSelector(
    [baseRegionSelector, (state: RegionState, includeRegionLink: boolean) => includeRegionLink],
    ({ region, categoryId }: BaseRegionSelectors, includeRegionLink: boolean): Breadcrumb[] => {
        const { name, webPath } = region ?? {}

        const breadcrumbs: Breadcrumb[] = [
            { title: 'Home', url: '/' },
            { title: 'Region', url: '/region' },
        ]

        // Include region link only on region/city-category pages
        if (name && webPath && (categoryId || includeRegionLink)) {
            breadcrumbs.push({ title: name, url: webPath })
        }

        return breadcrumbs
    },
)

export const metadataSelector = createSelector(
    baseRegionSelector,
    ({
        region,
        categoryId,
        categoryWebPathMap,
        categoryOverrides,
        responsiveImageUrls,
    }: BaseRegionSelectors): Metadata => {
        const { name = '', webPath, meta: customMeta } = region ?? {}
        let metadata: Metadata

        if (categoryId) {
            // Build metadata for region/city-category pages
            const categoryDefaultMetadataTitleMap: CategoryIdMap<string> = {
                [CATEGORY_IDS.CONCERTS]: `Concerts in ${name} & ${name} Concert Tickets | Vivid Seats`,
                [CATEGORY_IDS.SPORTS]: `${name} Sports Tickets | Vivid Seats`,
                [CATEGORY_IDS.THEATER]: `${name} Theater Tickets - ${name} Theater Show Dates | Vivid Seats`,
            }

            const categoryDefaultMetadataDescriptionMap: CategoryIdMap<string> = {
                [CATEGORY_IDS.CONCERTS]: `The best concerts come to ${name} and Vivid Seats has tickets for everyone! View seats for all ${name} venues and find your tickets today. 100% Buyer Guarantee.`,
                [CATEGORY_IDS.SPORTS]: `Find great tickets for all ${name} sports events, premium and sold-out. Find the best seats with our interactive seating chart and get your tickets at Vivid Seats today. 100% Buyer Guarantee.`,
                [CATEGORY_IDS.THEATER]: `The best theater tours come to ${name} and Vivid Seats has tickets for everyone! View seats for all ${name} venues and find your ${name} theater tickets today. 100% Buyer Guarantee.`,
            }

            // For region/city-category pages we need to check overrides first
            metadata = {
                title: categoryOverrides?.meta?.title || categoryDefaultMetadataTitleMap[categoryId],
                description: categoryOverrides?.meta?.description || categoryDefaultMetadataDescriptionMap[categoryId],
                keywords: categoryOverrides?.meta?.keywords,
                image: responsiveImageUrls.imageUrl,
                url: categoryWebPathMap[categoryId],
            }
        } else {
            // Build metadata for region/city pages
            metadata = {
                title: customMeta?.title || `${name} Tickets - ${name} Event Schedules | Vivid Seats`,
                description:
                    customMeta?.description ||
                    `Find great tickets for all ${name} events, premium and sold-out. View upcoming sports, concerts, and theater events in ${name}. 100% Buyer Guarantee.`,
                keywords: customMeta?.keywords,
                image: responsiveImageUrls.imageUrl,
                url: webPath,
            }
        }

        // For Next.js all undefined props must be excluded
        return pickBy(metadata, (value) => !!value)
    },
)
